import React, { useContext, useState } from 'react'
import moment from 'moment'
import AddNewMOT from './AddNewMOT'
import AttachmentTiles from './AttachmentTiles'
import BigModal from '../../../../../components/Modals/BigModal'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import EditIcon from '@mui/icons-material/Edit'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import NumbersRoundedIcon from '@mui/icons-material/NumbersRounded'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import StatCard from '../../../../../components/Cards/StatCard'
import { TenantSettingsContext } from '../../../../../App'

const MotModal = ({ selectedMOT, setSelectedMOT }) => {
  const open = !!selectedMOT
  const pass = !!selectedMOT?.pass
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const [editMOT, setEditMOT] = useState(false)

  const dateLabel = selectedMOT?.date
    ? moment(selectedMOT.date).format('DD/MM/YYYY')
    : '—'

  const close = () => setSelectedMOT(null)

  return (
    <>
      <BigModal open={open} onClose={close} hideBackgroundColor>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'background.default',
            border: (t) =>
              `1px solid ${
                t.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.10)'
                  : 'rgba(0,0,0,0.10)'
              }`,
            // helps prevent content from forcing weird layouts in modals
            width: '100%',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 3, py: 2, bgcolor: 'background.paper' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="title2"
                  sx={{ fontWeight: 900, letterSpacing: -0.3 }}
                  noWrap
                >
                  MOT
                </Typography>

                <Chip
                  icon={
                    pass ? <CheckCircleRoundedIcon /> : <CancelRoundedIcon />
                  }
                  label={pass ? 'Pass' : 'Fail'}
                  color={pass ? 'success' : 'error'}
                  sx={{
                    fontWeight: 800,
                    '& .MuiChip-icon': { ml: 0.8 },
                    borderRadius: 1,
                  }}
                />
              </Stack>

              <Stack direction="row" gap={1} alignItems="center">
                <IconButton
                  onClick={() => setEditMOT(true)}
                  size="small"
                  aria-label="Edit"
                >
                  <EditIcon />
                </IconButton>
                <IconButton onClick={close} size="small" aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          <Divider sx={{ opacity: 0.6 }} />

          {/* Body */}
          <Box
            sx={{
              p: 3,
              // prevents overlap in shorter viewports by allowing scroll
              maxHeight: '75vh',
              overflowY: 'auto',
            }}
          >
            {/* Stat cards — Grid prevents overlap by wrapping */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={CalendarMonthRoundedIcon}
                  label="Date"
                  value={dateLabel}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={SpeedRoundedIcon}
                  label="Mileage"
                  value={
                    selectedMOT?.mileage !== undefined &&
                    selectedMOT?.mileage !== null
                      ? `${selectedMOT.mileage}`
                      : '—'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={PlaceRoundedIcon}
                  label="Test Location"
                  value={selectedMOT?.testLocation ?? '—'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={NumbersRoundedIcon}
                  label="Test Number"
                  value={selectedMOT?.testNumber ?? '—'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <StatCard
                  icon={PaidRoundedIcon}
                  label="Cost"
                  value={
                    selectedMOT?.cost != null
                      ? `${orgSettings?.currency ?? '£'}${Number(selectedMOT.cost).toFixed(2)}`
                      : '—'
                  }
                />
              </Grid>

              {/* Advisories */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: (t) =>
                      t.palette.mode === 'dark'
                        ? 'none'
                        : '0 2px 10px rgba(0,0,0,0.04)',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ opacity: 0.65, letterSpacing: 0.8 }}
                  >
                    Advisories
                  </Typography>

                  {selectedMOT?.advisories?.trim() ? (
                    <Typography
                      variant="body1"
                      sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}
                    >
                      {selectedMOT.advisories}
                    </Typography>
                  ) : (
                    <Stack
                      sx={{ mt: 2 }}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <CheckCircleRoundedIcon
                        color="success"
                        fontSize="small"
                      />
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.7, fontStyle: 'italic' }}
                      >
                        No advisories recorded.
                      </Typography>
                    </Stack>
                  )}
                </Paper>
              </Grid>

              {/* Attachments */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: (t) =>
                      t.palette.mode === 'dark'
                        ? 'none'
                        : '0 2px 10px rgba(0,0,0,0.04)',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="overline"
                      sx={{ opacity: 0.65, letterSpacing: 0.8 }}
                    >
                      Attachments
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.65 }}>
                      {selectedMOT?.documents?.length
                        ? `${selectedMOT.documents.length} file(s)`
                        : 'No files'}
                    </Typography>
                  </Stack>

                  <AttachmentTiles documents={selectedMOT?.documents} small />
                </Paper>
              </Grid>
            </Grid>

            {selectedMOT?.updatedAt && (
              <Typography
                variant="body2"
                sx={{ mt: 1.5, opacity: 0.7 }}
                noWrap
                textAlign="end"
              >
                Updated by {selectedMOT?.updatedBy?.firstName}{' '}
                {selectedMOT?.updatedBy?.lastName} at{' '}
                {moment(selectedMOT.updatedAt).format('HH:mm [on] Do MMM YY')}
              </Typography>
            )}
          </Box>
        </Paper>
      </BigModal>
      <BigModal open={editMOT} onClose={() => setEditMOT(false)}>
        <AddNewMOT
          existingMOT={selectedMOT}
          onClose={() => setEditMOT(false)}
        />
      </BigModal>
    </>
  )
}

export default MotModal
